import { API_URL } from "../config/api";

// Google Address Schema interface
export interface GoogleAddressSchema {
  place_id: string;
  formatted_address: string;
  street_number: string;
  route: string;
  city: string;
  state: string;
  locality: string;
  administrative_area_level_1: string;
  country: string;
  postal_code: string;
  latitude: number;
  longitude: number;
  timezone: string;
  address_components?: Array<{
    long_name: string;
    short_name: string;
    types: string[];
  }>;
}

// Address autocomplete response
export interface AddressAutocompleteResponse {
  predictions: Array<{
    place_id: string;
    description: string;
    structured_formatting: {
      main_text: string;
      secondary_text: string;
    };
  }>;
}

// Nearby search response
export interface NearbySearchResponse {
  results: Array<{
    place_id: string;
    name: string;
    formatted_address: string;
    geometry: {
      location: {
        lat: number;
        lng: number;
      };
    };
    types: string[];
  }>;
}

/**
 * Autocomplete typing address
 * @param {string} token - The authorization token
 * @param {string} searchQuery - The search query for autocomplete
 * @returns {Promise<AddressAutocompleteResponse>} Autocomplete suggestions
 */
export const autocompleteAddress = async (
  token: string,
  searchQuery: string
): Promise<AddressAutocompleteResponse> => {
  try {
    console.log('Autocompleting address:', searchQuery);
    
    const params = new URLSearchParams({
      search_query: searchQuery
    });

    const r = await fetch(`${API_URL}/address/autocomplete?${params.toString()}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      console.error('Failed to autocomplete address:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: AddressAutocompleteResponse = await r.json();
    console.log('Address autocomplete result:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in autocompleteAddress:', e);
    throw new Error(e.message || 'Failed to autocomplete address');
  }
};

/**
 * Get place details by place_id
 * @param {string} token - The authorization token
 * @param {string} placeId - The Google place ID
 * @returns {Promise<GoogleAddressSchema>} Detailed place information
 */
export const getPlaceDetails = async (
  token: string,
  placeId: string
): Promise<GoogleAddressSchema> => {
  try {
    console.log('Getting place details for:', placeId);
    
    const params = new URLSearchParams({
      place_id: placeId
    });

    const r = await fetch(`${API_URL}/address/details?${params.toString()}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      console.error('Failed to get place details:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: GoogleAddressSchema = await r.json();
    console.log('Place details result:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in getPlaceDetails:', e);
    throw new Error(e.message || 'Failed to get place details');
  }
};

/**
 * Search nearby places
 * @param {string} token - The authorization token
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @param {number} radiusMeters - Search radius in meters (default: 1000)
 * @param {string[]} includeTypes - Array of place types to include
 * @returns {Promise<NearbySearchResponse>} Nearby places
 */
export const searchNearbyPlaces = async (
  token: string,
  lat: number,
  lng: number,
  radiusMeters: number = 1000,
  includeTypes: string[] = []
): Promise<NearbySearchResponse> => {
  try {
    console.log('Searching nearby places:', { lat, lng, radiusMeters, includeTypes });
    
    const params = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString(),
      radius_meters: radiusMeters.toString()
    });

    // Add include_types if provided
    includeTypes.forEach(type => {
      params.append('include_types', type);
    });

    const r = await fetch(`${API_URL}/address/nearby?${params.toString()}`, {
      headers: {
        'content-type': 'application/json',
        'authorization': 'Bearer ' + token
      }
    });

    if (!r.ok) {
      console.error('Failed to search nearby places:', r.status, r.statusText);
      throw new Error(`HTTP ${r.status}: ${r.statusText}`);
    }

    const data: NearbySearchResponse = await r.json();
    console.log('Nearby search result:', data);
    return data;
  } catch (e: Error | any) {
    console.error('Error in searchNearbyPlaces:', e);
    throw new Error(e.message || 'Failed to search nearby places');
  }
}; 